import { Injectable, computed, inject, signal } from "@angular/core";
import { MsalBroadcastService, MsalService } from "@azure/msal-angular";
import { InteractionStatus } from "@azure/msal-browser";
import { filter, firstValueFrom } from "rxjs";
import { AccountWithGroupClaims } from "../models/claims-account.model";
import { groups } from "../auth-config";
import { Consultant } from "../models/consultant.model";
import { ConsultantService } from "./consultant.service";
import { diagMsal } from "../loop-diagnostics";

/**
 * Owns "who is signed in": the MSAL account and the Consultant record it maps to.
 *
 * This service does not process login redirects. That is done where MSAL's own
 * documentation puts it — MsalRedirectComponent, and MsalGuard on guarded routes —
 * and doing it here as well made a third handler, each of which also initialised
 * MSAL. This waits for MSAL to come to rest after that work, then reads the outcome.
 *
 * Resolved once per app load. Re-authentication is a full page redirect, so the app
 * boots and resolves again by itself; re-resolving on MSAL activity fed itself,
 * because the consultant lookup acquires a token, which is MSAL activity.
 */
@Injectable({
    providedIn: 'root'
})
export class AuthService {
    private readonly msal = inject(MsalService);
    private readonly msalBroadcast = inject(MsalBroadcastService);
    private readonly consultants = inject(ConsultantService);

    readonly activeAccount = signal<AccountWithGroupClaims | null>(null);
    readonly consultant = signal<Consultant | null>(null);

    readonly isConsultant = computed(() => this.activeAccount()?.idTokenClaims?.groups?.includes(groups.consultant) ?? false);
    readonly isAdmin = computed(() => this.activeAccount()?.idTokenClaims?.groups?.includes(groups.admin) ?? false);
    readonly hasDashboardAccess = computed(() => this.consultant()?.dashboardAccess === true);

    private initialization?: Promise<void>;

    /** homeAccountId the identity is resolved (or being resolved) for. */
    private resolvedFor: string | null = null;

    /**
     * Resolves the signed-in identity. Safe to call from anywhere, any number of times:
     * the work happens once, and callers that arrive later (route guards during
     * bootstrap) wait for the same result instead of racing it.
     */
    whenReady(): Promise<void> {
        this.initialization ??= this.initialize().catch(error => {
            console.error('Kunne ikke initialisere login', error);
            // Leave the door open for a retry rather than caching the failure forever.
            this.initialization = undefined;
        });

        return this.initialization;
    }

    private async initialize(): Promise<void> {
        // inProgress$ starts at Startup and first reaches None once the redirect
        // response, if any, has been processed by its owner.
        await firstValueFrom(this.msalBroadcast.inProgress$.pipe(
            filter(status => status === InteractionStatus.None)));

        const account = this.currentMsalAccount();
        diagMsal('auth:resolve', this.msal.instance);

        await this.resolveIdentity(account);
    }

    private currentMsalAccount(): AccountWithGroupClaims | null {
        const active = this.msal.instance.getActiveAccount();
        if (active)
            return active;

        // A browser that has collected several cached accounts must not be resolved
        // against whichever happens to sit at index 0: a stale entry there cannot renew
        // a token silently. The freshest sign-in is the one actually being used.
        return this.msal.instance.getAllAccounts()
            .reduce<AccountWithGroupClaims | null>(
                (newest, account) => authenticatedAt(account) > authenticatedAt(newest) ? account : newest,
                null);
    }

    private async resolveIdentity(account: AccountWithGroupClaims | null): Promise<void> {
        if (!account) {
            this.resolvedFor = null;
            this.activeAccount.set(null);
            this.consultant.set(null);
            return;
        }

        this.resolvedFor = account.homeAccountId;

        this.msal.instance.setActiveAccount(account);
        this.activeAccount.set(account);

        await this.loadConsultant(account);
    }

    private async loadConsultant(account: AccountWithGroupClaims): Promise<void> {
        try {
            const consultant = await this.consultants.getByEmail(account.username);

            // The account may have changed while the request was in flight.
            if (this.resolvedFor !== account.homeAccountId)
                return;

            this.consultant.set(consultant);
        } catch (error) {
            // A failed lookup must not take the whole app down with it: the user gets the
            // app with the consultant-gated parts hidden until the next load.
            console.error('Kunne ikke hente konsulentprofil', error);

            if (this.resolvedFor !== account.homeAccountId)
                return;

            this.consultant.set(null);
        }
    }
}

/** When an account last authenticated; only used to prefer the freshest of several. */
function authenticatedAt(account: AccountWithGroupClaims | null): number {
    const claims = account?.idTokenClaims as { auth_time?: number, iat?: number } | undefined;
    return claims?.auth_time ?? claims?.iat ?? 0;
}
